import React, { useEffect, useState } from 'react';
import { useNavigate, Navigate } from 'react-router';
import { useTheme, createThemedStyles } from '../contexts/ThemeContext';
import { useFeatureFlagContext } from '../contexts/FeatureFlagContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faTrash, faCheckCircle, faCircle } from '@/icons';
import { SignupProgress, TEAM_SIGNUP_STEPS } from '../components/SignupProgress';
import { SignupBackButton } from '../components/SignupBackButton';
import { CustomToggle } from '../components/CustomToggle';
import { TurnstileWidget } from '../components/TurnstileWidget';
import { useSignupSession, SignupMember } from '../contexts/SignupSessionContext';
import { authApiService } from '../services/authApiService';
import { config } from '../config/environment';
import { debugError } from '../utils/debug';

interface MemberRow extends SignupMember {
    id: string;
}

type RoleOption = { value: 'attorney' | 'supportStaff'; label: string };

const ROLE_OPTIONS: RoleOption[] = [
    { value: 'attorney', label: 'Attorney' },
    { value: 'supportStaff', label: 'Support Staff' },
];

let idCounter = 0;
function nextId() { return String(++idCounter); }

function blankMember(): MemberRow {
    return { id: nextId(), firstName: '', lastName: '', email: '', role: 'attorney', isAdminContact: false };
}

function isValidEmail(e: string) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e.trim());
}

export const RegisterTeamPage: React.FC = () => {
    const navigate = useNavigate();
    const theme = useTheme();
    const themedStyles = createThemedStyles(theme);
    const { flags, isLoading: flagsLoading } = useFeatureFlagContext();
    const selfServeSignup = flags['SelfServiceSignup'] ?? false;
    const { session, updateSession } = useSignupSession();

    const [teamName, setTeamName] = useState(session.teamName || '');
    const [founderFirst, setFounderFirst] = useState(session.firstName || '');
    const [founderLast, setFounderLast] = useState(session.lastName || '');
    const [founderEmail, setFounderEmail] = useState(session.email || '');
    const [founderRole, setFounderRole] = useState<'attorney' | 'supportStaff'>(session.founderRole || 'attorney');
    const [members, setMembers] = useState<MemberRow[]>(() =>
        session.members.length > 0
            ? session.members.map(m => ({ ...m, id: nextId() }))
            : []
    );
    const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
    const [touched, setTouched] = useState(false);

    useEffect(() => {
        document.title = 'Build your team | Calculist';
    }, []);

    if (flagsLoading) return null;
    if (!selfServeSignup) return <Navigate to="/auth/login" replace />;
    if (!session.plan) return <Navigate to="/auth/register/plan" replace />;

    const hasAtLeastOneAttorney = founderRole === 'attorney' || members.some(m => m.role === 'attorney');

    const updateMember = (id: string, updates: Partial<MemberRow>) => {
        setMembers(prev => prev.map(m => m.id === id ? { ...m, ...updates } : m));
    };

    const removeMember = (id: string) => {
        setMembers(prev => prev.filter(m => m.id !== id));
    };

    const addMember = () => {
        setTouched(true);
        setMembers(prev => [...prev, blankMember()]);
    };

    const memberErrors = (m: MemberRow): { firstName?: string; lastName?: string; email?: string } => {
        const e: { firstName?: string; lastName?: string; email?: string } = {};
        if (m.firstName.trim().length > 0 && m.firstName.trim().length < 2) e.firstName = 'Min 2 characters.';
        if (m.lastName.trim().length > 0 && m.lastName.trim().length < 2) e.lastName = 'Min 2 characters.';
        if (m.email.trim() && !isValidEmail(m.email)) e.email = 'Invalid email format.';
        return e;
    };

    const hasMemberErrors = members.some(m => {
        const e = memberErrors(m);
        return Object.keys(e).length > 0
            || m.firstName.trim().length < 2
            || m.lastName.trim().length < 2
            || !isValidEmail(m.email);
    });

    const validate = (): boolean => {
        const errs: Record<string, string> = {};
        if (!teamName.trim()) errs['teamName'] = 'Team name is required.';
        if (founderFirst.trim().length < 2) errs['founderFirst'] = 'Min 2 characters.';
        if (founderLast.trim().length < 2) errs['founderLast'] = 'Min 2 characters.';
        if (!isValidEmail(founderEmail)) errs['founderEmail'] = 'Invalid email format.';
        const emails: string[] = [founderEmail.trim().toLowerCase()];
        members.forEach(m => {
            if (m.firstName.trim().length < 2) errs[`firstName-${m.id}`] = 'Min 2 characters.';
            if (m.lastName.trim().length < 2) errs[`lastName-${m.id}`] = 'Min 2 characters.';
            if (!isValidEmail(m.email)) errs[`email-${m.id}`] = 'Invalid email format.';
            const lower = m.email.trim().toLowerCase();
            if (lower && emails.includes(lower)) {
                errs[`email-${m.id}`] = 'Duplicate email.';
            } else if (lower) {
                emails.push(lower);
            }
        });
        setFieldErrors(errs);
        return Object.keys(errs).length === 0;
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setTouched(true);
        if (!validate()) return;
        setError(null);
        setSubmitting(true);
        try {
            const { data: startData } = await authApiService.signupRegisterStart({
                email: founderEmail.trim(),
                firstName: founderFirst.trim(),
                lastName: founderLast.trim(),
                turnstileToken,
            });
            const token = (startData as { sessionToken?: string }).sessionToken ?? '';

            await authApiService.signupSavePlan({
                sessionToken: token,
                plan: session.plan!,
            });

            const cleanMembers: SignupMember[] = members
                .filter(m => m.firstName.trim() || m.lastName.trim() || m.email.trim())
                .map(({ id: _id, ...rest }) => ({
                    ...rest,
                    firstName: rest.firstName.trim(),
                    lastName: rest.lastName.trim(),
                    email: rest.email.trim(),
                }));

            await authApiService.signupSaveTeam({
                sessionToken: token,
                teamName: teamName.trim(),
                members: cleanMembers,
                founderRole,
                founderIsAdminContact: true,
            });

            updateSession({
                sessionToken: token,
                email: founderEmail.trim(),
                firstName: founderFirst.trim(),
                lastName: founderLast.trim(),
                teamName: teamName.trim(),
                members: cleanMembers,
                founderRole,
            });
            navigate('/auth/register/payment');
        } catch (err: unknown) {
            debugError('signupTeamSubmit', err);
            setError(err instanceof Error ? err.message : 'Could not save team. Please try again.');
        } finally {
            setSubmitting(false);
        }
    };

    const canContinue = hasAtLeastOneAttorney && !hasMemberErrors && (!config.turnstileSiteKey || !!turnstileToken);

    return (
        <div style={{
            minHeight: '100vh',
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'center',
            backgroundColor: theme.colors.background,
            padding: theme.spacing.md,
            paddingTop: theme.spacing.xl,
        }}>
            <div style={{ ...themedStyles.card.default, maxWidth: '900px', width: '100%' }}>
                <SignupProgress currentStep={2} steps={TEAM_SIGNUP_STEPS} />

                <div style={{ textAlign: 'center', marginBottom: theme.spacing.xl }}>
                    <img src="/logo-name.svg" alt="Calculist" style={{ height: '48px', width: 'auto', marginBottom: theme.spacing.md }} />
                    <h1 style={{ ...themedStyles.text.heading, marginTop: 0, marginBottom: theme.spacing.xs }}>Build your team</h1>
                    <p style={{ ...themedStyles.text.muted, marginTop: 0 }}>Tell us about your team. You can always add more members later.</p>
                </div>

                {error && (
                    <div style={{ ...themedStyles.alert.error, marginBottom: theme.spacing.md }}>
                        <span>⚠️</span>
                        <div style={{ flex: 1 }}>{error}</div>
                    </div>
                )}

                <form onSubmit={(e) => void handleSubmit(e)} style={{ display: 'flex', flexDirection: 'column', gap: theme.spacing.lg }}>
                    {/* Team name */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: theme.spacing.xs }}>
                        <label style={themedStyles.text.label}>Team name <span style={{ color: theme.colors.status.error }}>*</span></label>
                        <input
                            value={teamName}
                            onChange={e => setTeamName(e.target.value)}
                            placeholder="My Law Firm, LLC"
                            disabled={submitting}
                            style={themedStyles.input.default}
                        />
                        {fieldErrors['teamName'] && (
                            <span style={{ fontSize: theme.typography.fontSize.xs, color: theme.colors.status.error }}>{fieldErrors['teamName']}</span>
                        )}
                    </div>

                    {/* Team Members */}
                    <div>
                        <div style={{ fontWeight: theme.typography.fontWeight.semibold, color: theme.colors.text.primary, marginBottom: theme.spacing.sm }}>
                            Team Members
                        </div>

                        <div style={{ display: 'flex', flexDirection: 'column', gap: theme.spacing.sm, marginBottom: theme.spacing.sm }}>
                            {/* Founder row — locked at top */}
                            <div style={{
                                border: `2px solid ${theme.colors.primary}`,
                                borderRadius: theme.borderRadius.lg,
                                padding: theme.spacing.md,
                                backgroundColor: theme.colors.primary + '08',
                                display: 'flex',
                                flexDirection: 'column',
                                gap: theme.spacing.sm,
                            }}>
                                <div style={{ fontSize: theme.typography.fontSize.xs, fontWeight: theme.typography.fontWeight.semibold, color: theme.colors.primary, marginBottom: '2px' }}>
                                    You (Account Owner)
                                </div>
                                {/* Row 1: First Name, Last Name, Email */}
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 2fr', gap: theme.spacing.sm }}>
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                                        <label style={{ ...themedStyles.text.label, fontSize: theme.typography.fontSize.xs }}>First Name</label>
                                        <input
                                            value={founderFirst}
                                            onChange={e => setFounderFirst(e.target.value)}
                                            placeholder="First"
                                            disabled={submitting}
                                            style={{
                                                ...themedStyles.input.default,
                                                borderColor: fieldErrors['founderFirst'] ? theme.colors.status.error : undefined,
                                            }}
                                        />
                                        {fieldErrors['founderFirst'] && <span style={{ fontSize: '10px', color: theme.colors.status.error }}>{fieldErrors['founderFirst']}</span>}
                                    </div>
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                                        <label style={{ ...themedStyles.text.label, fontSize: theme.typography.fontSize.xs }}>Last Name</label>
                                        <input
                                            value={founderLast}
                                            onChange={e => setFounderLast(e.target.value)}
                                            placeholder="Last"
                                            disabled={submitting}
                                            style={{
                                                ...themedStyles.input.default,
                                                borderColor: fieldErrors['founderLast'] ? theme.colors.status.error : undefined,
                                            }}
                                        />
                                        {fieldErrors['founderLast'] && <span style={{ fontSize: '10px', color: theme.colors.status.error }}>{fieldErrors['founderLast']}</span>}
                                    </div>
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                                        <label style={{ ...themedStyles.text.label, fontSize: theme.typography.fontSize.xs }}>Email</label>
                                        <input
                                            value={founderEmail}
                                            onChange={e => setFounderEmail(e.target.value)}
                                            placeholder="email@firm.com"
                                            type="email"
                                            disabled={submitting}
                                            style={{
                                                ...themedStyles.input.default,
                                                borderColor: fieldErrors['founderEmail'] ? theme.colors.status.error : undefined,
                                            }}
                                        />
                                        {fieldErrors['founderEmail'] && <span style={{ fontSize: '10px', color: theme.colors.status.error }}>{fieldErrors['founderEmail']}</span>}
                                    </div>
                                </div>
                                {/* Row 2: Role + Admin Contact (locked) */}
                                <div style={{ display: 'flex', alignItems: 'center', gap: theme.spacing.md }}>
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                                        <label style={{ ...themedStyles.text.label, fontSize: theme.typography.fontSize.xs }}>Role</label>
                                        <div style={{ display: 'flex', gap: theme.spacing.sm }}>
                                            {ROLE_OPTIONS.map(opt => {
                                                const isSelected = founderRole === opt.value;
                                                return (
                                                    <label key={opt.value} style={{
                                                        display: 'flex', alignItems: 'center', gap: '6px',
                                                        padding: `6px ${theme.spacing.sm}`,
                                                        borderRadius: theme.borderRadius.md,
                                                        border: `1px solid ${isSelected ? theme.colors.primary : theme.colors.border.default}`,
                                                        backgroundColor: isSelected ? theme.colors.primary : theme.colors.surface,
                                                        cursor: submitting ? 'not-allowed' : 'pointer',
                                                        opacity: submitting ? 0.6 : 1,
                                                        userSelect: 'none',
                                                    }}>
                                                        <input type="radio" name="founderRole" value={opt.value} checked={isSelected}
                                                            onChange={() => setFounderRole(opt.value)} disabled={submitting}
                                                            style={{ position: 'absolute', opacity: 0, width: 0, height: 0 }} />
                                                        <FontAwesomeIcon icon={isSelected ? faCheckCircle : faCircle}
                                                            style={{ fontSize: '14px', color: isSelected ? 'white' : theme.colors.text.muted }} />
                                                        <span style={{ fontSize: theme.typography.fontSize.sm, color: isSelected ? 'white' : theme.colors.text.primary, fontWeight: isSelected ? theme.typography.fontWeight.medium : theme.typography.fontWeight.normal }}>
                                                            {opt.label}
                                                        </span>
                                                    </label>
                                                );
                                            })}
                                        </div>
                                    </div>
                                    <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: theme.spacing.md }}>
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                                            <label style={{ ...themedStyles.text.label, fontSize: theme.typography.fontSize.xs }}>Admin Contact</label>
                                            <div style={{ paddingTop: '4px' }}>
                                                <CustomToggle
                                                    checked={true}
                                                    onChange={() => {}}
                                                    label=""
                                                    disabled={true}
                                                    style={{ border: 'none', padding: 0, backgroundColor: 'transparent' }}
                                                />
                                            </div>
                                        </div>
                                        <div style={{ fontSize: theme.typography.fontSize.xs, color: theme.colors.text.muted, paddingTop: '18px' }}>
                                            Account owner is always the admin contact
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Additional members */}
                            {members.map(m => {
                                const errs = memberErrors(m);
                                return (
                                    <div key={m.id} style={{
                                        border: `1px solid ${theme.colors.border.default}`,
                                        borderRadius: theme.borderRadius.lg,
                                        padding: theme.spacing.md,
                                        backgroundColor: theme.colors.surface,
                                        display: 'flex',
                                        flexDirection: 'column',
                                        gap: theme.spacing.sm,
                                    }}>
                                        {/* Row 1: First Name, Last Name, Email */}
                                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 2fr', gap: theme.spacing.sm }}>
                                            <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                                                <label style={{ ...themedStyles.text.label, fontSize: theme.typography.fontSize.xs }}>First Name</label>
                                                <input
                                                    value={m.firstName}
                                                    onChange={e => updateMember(m.id, { firstName: e.target.value })}
                                                    placeholder="First"
                                                    disabled={submitting}
                                                    style={{
                                                        ...themedStyles.input.default,
                                                        borderColor: errs.firstName ? theme.colors.status.error : undefined,
                                                    }}
                                                />
                                                {errs.firstName && <span style={{ fontSize: '10px', color: theme.colors.status.error }}>{errs.firstName}</span>}
                                            </div>
                                            <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                                                <label style={{ ...themedStyles.text.label, fontSize: theme.typography.fontSize.xs }}>Last Name</label>
                                                <input
                                                    value={m.lastName}
                                                    onChange={e => updateMember(m.id, { lastName: e.target.value })}
                                                    placeholder="Last"
                                                    disabled={submitting}
                                                    style={{
                                                        ...themedStyles.input.default,
                                                        borderColor: errs.lastName ? theme.colors.status.error : undefined,
                                                    }}
                                                />
                                                {errs.lastName && <span style={{ fontSize: '10px', color: theme.colors.status.error }}>{errs.lastName}</span>}
                                            </div>
                                            <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                                                <label style={{ ...themedStyles.text.label, fontSize: theme.typography.fontSize.xs }}>Email</label>
                                                <input
                                                    value={m.email}
                                                    onChange={e => updateMember(m.id, { email: e.target.value })}
                                                    placeholder="email@firm.com"
                                                    type="email"
                                                    disabled={submitting}
                                                    style={{
                                                        ...themedStyles.input.default,
                                                        borderColor: errs.email ? theme.colors.status.error : undefined,
                                                    }}
                                                />
                                                {errs.email && <span style={{ fontSize: '10px', color: theme.colors.status.error }}>{errs.email}</span>}
                                            </div>
                                        </div>

                                        {/* Row 2: Role, Admin Contact, Delete */}
                                        <div style={{ display: 'flex', alignItems: 'center', gap: theme.spacing.md }}>
                                            <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                                                <label style={{ ...themedStyles.text.label, fontSize: theme.typography.fontSize.xs }}>Role</label>
                                                <div style={{ display: 'flex', gap: theme.spacing.sm }}>
                                                    {ROLE_OPTIONS.map(opt => {
                                                        const isSelected = m.role === opt.value;
                                                        return (
                                                            <label key={opt.value} style={{
                                                                display: 'flex', alignItems: 'center', gap: '6px',
                                                                padding: `6px ${theme.spacing.sm}`,
                                                                borderRadius: theme.borderRadius.md,
                                                                border: `1px solid ${isSelected ? theme.colors.primary : theme.colors.border.default}`,
                                                                backgroundColor: isSelected ? theme.colors.primary : theme.colors.surface,
                                                                cursor: submitting ? 'not-allowed' : 'pointer',
                                                                opacity: submitting ? 0.6 : 1,
                                                                userSelect: 'none',
                                                            }}>
                                                                <input type="radio" name={`role-${m.id}`} value={opt.value} checked={isSelected}
                                                                    onChange={() => updateMember(m.id, { role: opt.value })} disabled={submitting}
                                                                    style={{ position: 'absolute', opacity: 0, width: 0, height: 0 }} />
                                                                <FontAwesomeIcon icon={isSelected ? faCheckCircle : faCircle}
                                                                    style={{ fontSize: '14px', color: isSelected ? 'white' : theme.colors.text.muted }} />
                                                                <span style={{ fontSize: theme.typography.fontSize.sm, color: isSelected ? 'white' : theme.colors.text.primary, fontWeight: isSelected ? theme.typography.fontWeight.medium : theme.typography.fontWeight.normal }}>
                                                                    {opt.label}
                                                                </span>
                                                            </label>
                                                        );
                                                    })}
                                                </div>
                                            </div>
                                            <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: theme.spacing.md }}>
                                                <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                                                    <label style={{ ...themedStyles.text.label, fontSize: theme.typography.fontSize.xs }}>Admin Contact</label>
                                                    <div style={{ paddingTop: '4px' }}>
                                                        <CustomToggle
                                                            checked={m.isAdminContact}
                                                            onChange={val => updateMember(m.id, { isAdminContact: val })}
                                                            label=""
                                                            disabled={submitting}
                                                            style={{ border: 'none', padding: 0, backgroundColor: 'transparent' }}
                                                        />
                                                    </div>
                                                </div>
                                                <button
                                                    type="button"
                                                    onClick={() => removeMember(m.id)}
                                                    disabled={submitting}
                                                    style={{
                                                        background: 'none',
                                                        border: 'none',
                                                        cursor: submitting ? 'not-allowed' : 'pointer',
                                                        color: theme.colors.status.error,
                                                        padding: '4px 8px',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: theme.spacing.xs,
                                                        fontSize: theme.typography.fontSize.sm,
                                                        marginTop: '18px',
                                                    }}
                                                    aria-label="Remove member"
                                                >
                                                    <FontAwesomeIcon icon={faTrash} style={{ fontSize: '13px' }} />
                                                    Remove
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>

                        <div style={{ display: 'flex', alignItems: 'center', gap: theme.spacing.md }}>
                        <button
                            type="button"
                            onClick={addMember}
                            disabled={submitting}
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: theme.spacing.xs,
                                padding: `${theme.spacing.sm} ${theme.spacing.md}`,
                                backgroundColor: theme.colors.secondary,
                                border: 'none',
                                borderRadius: theme.borderRadius.md,
                                color: theme.colors.text.inverse,
                                cursor: submitting ? 'not-allowed' : 'pointer',
                                fontSize: theme.typography.fontSize.sm,
                                fontWeight: theme.typography.fontWeight.medium,
                            }}
                        >
                            <FontAwesomeIcon icon={faPlus} />
                            Add Member
                        </button>
                        <span style={{ fontSize: theme.typography.fontSize.xs, color: theme.colors.text.muted }}>
                            (You can also add more team members later)
                        </span>
                        </div>

                    </div>

                    {!!config.turnstileSiteKey && (
                        <TurnstileWidget siteKey={config.turnstileSiteKey} onTokenChange={setTurnstileToken} />
                    )}

                    {!hasAtLeastOneAttorney && (
                        <p style={{ margin: 0, fontSize: theme.typography.fontSize.sm, fontWeight: theme.typography.fontWeight.semibold, color: theme.colors.status.error }}>
                            At least one team member must have the Attorney role before you can continue.
                        </p>
                    )}

                    <button
                        type="submit"
                        disabled={submitting || !canContinue}
                        style={{
                            ...themedStyles.button.primary,
                            ...(submitting || !canContinue ? themedStyles.button.disabled : {}),
                            padding: `${theme.spacing.md} ${theme.spacing.xl}`,
                        }}
                    >
                        {submitting ? 'Saving…' : 'Continue'}
                    </button>
                    <SignupBackButton onClick={() => navigate('/auth/register/plan')} disabled={submitting} />
                </form>
            </div>
        </div>
    );
};
